'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Mic, MicOff, X, Sparkles, CheckCircle2, AlertTriangle, Volume2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  type A11ySettings,
  DEFAULT_A11Y_SETTINGS,
  readA11ySettingsFromStorage,
  applyA11ySettingsToDocument,
} from '@/lib/lyrra-a11y-settings';
import {
  hasActiveNonDefaultA11ySettings,
  isA11yResetIntent,
} from '@/lib/a11y-voice-guide-intent';
import {
  LYRRA_A11Y_STORAGE_KEY,
  LYRRA_A11Y_UPDATED_EVENT,
} from '@/hooks/useKeyboardNavigationSetting';

// ── API helpers ────────────────────────────────────────────────────────────

const PROMPT_ENDPOINT = '/api/a11y/voice-guide/prompt';
const REFORMULATE_ENDPOINT = '/api/a11y/voice-guide/reformulate';
const RECOMMEND_ENDPOINT = '/api/a11y/voice-guide/recommend';
const SPEAK_DYNAMIC_ENDPOINT = '/api/tts/speak-selection';

type PromptKey =
  | 'welcome'
  | 'couldnt_hear'
  | 'not_understood'
  | 'applied'
  | 'reset_applied'
  | 'cancelled'
  | 'error';

async function fetchAudioStaticPrompt(key: PromptKey, language: string): Promise<Blob> {
  const res = await fetch(PROMPT_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'audio/mpeg' },
    body: JSON.stringify({ key, language }),
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Prompt audio HTTP ${res.status}`);
  return res.blob();
}

async function fetchAudioDynamic(text: string, language: string): Promise<Blob> {
  const res = await fetch(SPEAK_DYNAMIC_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'audio/mpeg' },
    body: JSON.stringify({ text, language }),
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Dynamic TTS HTTP ${res.status}`);
  return res.blob();
}

async function reformulateUserDescription(
  description: string,
  language: string,
  translate: (key: string, defaultValue: string) => string,
) {
  const res = await fetch(REFORMULATE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ description, language }),
    credentials: 'same-origin',
  });
  if (!res.ok) {
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      throw new Error(
        translate(
          'a11y.voiceGuide.error.serverUnavailable',
          'Le serveur Lyrra ne répond pas correctement. Vérifiez que le backend est déployé et démarré, puis réessayez.',
        ),
      );
    }
    let detail = '';
    try {
      const j = (await res.json()) as { error?: { message?: string }; message?: string };
      detail = j?.error?.message ?? j?.message ?? '';
    } catch {
      /* ignore non-JSON */
    }
    throw new Error(
      detail ||
        translate('accessibility.speakSelection.error', 'Lecture indisponible, réessayez plus tard.'),
    );
  }
  const json = (await res.json()) as {
    data?: { summary: string; confirmPrompt: string };
    summary?: string;
    confirmPrompt?: string;
  };
  const summary = json.data?.summary ?? json.summary;
  const confirmPrompt = json.data?.confirmPrompt ?? json.confirmPrompt;
  if (!summary || !confirmPrompt) throw new Error('Réponse reformulation invalide');
  return { summary, confirmPrompt };
}

async function recommendA11ySettings(
  description: string,
  language: string,
  translate: (key: string, defaultValue: string) => string,
) {
  const res = await fetch(RECOMMEND_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ description, language }),
    credentials: 'same-origin',
  });
  if (!res.ok) {
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      throw new Error(
        translate(
          'a11y.voiceGuide.error.serverUnavailable',
          'Le serveur Lyrra ne répond pas correctement. Vérifiez que le backend est déployé et démarré, puis réessayez.',
        ),
      );
    }
    throw new Error(translate('accessibility.speakSelection.error', 'Lecture indisponible, réessayez plus tard.'));
  }
  const json = (await res.json()) as {
    data?: { settings: Partial<A11ySettings>; explanation: string; resetToDefaults?: boolean };
    settings?: Partial<A11ySettings>;
    explanation?: string;
    resetToDefaults?: boolean;
  };
  const settings = (json.data?.settings ?? json.settings ?? {}) as Partial<A11ySettings>;
  const explanation = json.data?.explanation ?? json.explanation ?? '';
  const resetToDefaults = json.data?.resetToDefaults ?? json.resetToDefaults ?? false;
  return { settings, explanation, resetToDefaults };
}

// ── Web Speech API typings ─────────────────────────────────────────────────

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((this: unknown, ev: { results: { length: number;[i: number]: { isFinal: boolean;[i: number]: { transcript: string } } } }) => void) | null;
  onerror: ((this: unknown, ev: { error: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

// ── Reconnaissance « oui » / « non » multilingue ───────────────────────────

const YES_TOKENS = new Set([
  'oui', 'ouais', 'yep', 'ok', "d'accord", 'daccord', 'okay',
  'yes', 'yeah', 'yep', 'sure', 'confirm', 'confirmé',
  'sí', 'si', 'vale',
  'ja', 'jawohl',
  'sì',
  'sim',
  'نعم', 'أجل',
  'да',
  'так',
]);

const NO_TOKENS = new Set([
  'non', 'nope', 'pas du tout',
  'no', 'nah',
  'nein',
  'nicht',
  'não', 'nao',
  'لا', 'كلا',
  'нет', 'ні',
]);

function classifyYesNo(text: string): 'yes' | 'no' | 'unknown' {
  const t = text.trim().toLowerCase();
  if (!t) return 'unknown';
  for (const token of YES_TOKENS) if (t.includes(token)) return 'yes';
  for (const token of NO_TOKENS) if (t.includes(token)) return 'no';
  return 'unknown';
}

// ── État ────────────────────────────────────────────────────────────────────

type Stage =
  | 'idle'
  | 'introPlaying'
  | 'listening'
  | 'thinking'
  | 'confirmPlaying'
  | 'confirmListening'
  | 'applying'
  | 'done'
  | 'error';

interface A11yVoiceGuideProps {
  open: boolean;
  onClose: () => void;
}

const SILENCE_MS = 4000;
const MAX_DESCRIPTION_MS = 60_000;

// ── Composant ───────────────────────────────────────────────────────────────

const A11yVoiceGuide: React.FC<A11yVoiceGuideProps> = ({ open, onClose }) => {
  const { i18n, t } = useTranslation();
  const language = useMemo(
    () =>
      (i18n.resolvedLanguage || i18n.language || document.documentElement.lang || 'fr')
        .toString()
        .toLowerCase(),
    [i18n.resolvedLanguage, i18n.language],
  );

  const [stage, setStage] = useState<Stage>('idle');
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [summary, setSummary] = useState<string | null>(null);
  const [confirmPrompt, setConfirmPrompt] = useState<string | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [appliedKeys, setAppliedKeys] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [audioPermissionDenied, setAudioPermissionDenied] = useState(false);
  const [hasCustomSettings, setHasCustomSettings] = useState(() =>
    hasActiveNonDefaultA11ySettings(readA11ySettingsFromStorage()),
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const silenceTimerRef = useRef<number | null>(null);
  const hardStopTimerRef = useRef<number | null>(null);
  const transcriptRef = useRef<string>('');
  const lastUserDescriptionRef = useRef<string>('');
  const aliveRef = useRef<boolean>(true);

  const sttSupported = useMemo(() => !!getSpeechRecognitionCtor(), []);

  // ── Lifecycle teardown helpers ─────────────────────────────────────────

  const teardownAudio = useCallback(() => {
    try {
      audioRef.current?.pause();
    } catch {
      /* ignore */
    }
    audioRef.current = null;
    if (objectUrlRef.current) {
      try {
        URL.revokeObjectURL(objectUrlRef.current);
      } catch {
        /* ignore */
      }
      objectUrlRef.current = null;
    }
  }, []);

  const teardownRecognition = useCallback(() => {
    try {
      recognitionRef.current?.abort();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    if (silenceTimerRef.current) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (hardStopTimerRef.current) {
      window.clearTimeout(hardStopTimerRef.current);
      hardStopTimerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    teardownAudio();
    teardownRecognition();
    setStage('idle');
    setTranscript('');
    setInterim('');
    setSummary(null);
    setConfirmPrompt(null);
    setExplanation(null);
    setAppliedKeys([]);
    setErrorMsg(null);
    transcriptRef.current = '';
  }, [teardownAudio, teardownRecognition]);

  // ── Playback helpers ───────────────────────────────────────────────────

  const playBlob = useCallback(
    (blob: Blob) =>
      new Promise<void>((resolve, reject) => {
        teardownAudio();
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => {
          teardownAudio();
          resolve();
        };
        audio.onerror = () => {
          teardownAudio();
          reject(new Error('audio error'));
        };
        audio.play().catch(reject);
      }),
    [teardownAudio],
  );

  const playStaticPrompt = useCallback(
    async (key: PromptKey) => {
      try {
        const blob = await fetchAudioStaticPrompt(key, language);
        await playBlob(blob);
      } catch (e) {
        console.warn('[A11yVoiceGuide] static prompt failed', key, e);
      }
    },
    [language, playBlob],
  );

  const playDynamic = useCallback(
    async (text: string) => {
      try {
        const blob = await fetchAudioDynamic(text, language);
        await playBlob(blob);
      } catch (e) {
        console.warn('[A11yVoiceGuide] dynamic TTS failed', e);
      }
    },
    [language, playBlob],
  );

  // ── Reconnaissance vocale ──────────────────────────────────────────────

  const startRecognition = useCallback(
    (
      onFinal: (text: string) => void,
      onSilence?: () => void,
      opts?: { silenceMs?: number; hardStopMs?: number },
    ) => {
      const Ctor = getSpeechRecognitionCtor();
      if (!Ctor) {
        setErrorMsg(t('a11y.voiceGuide.error.stt', 'Reconnaissance vocale non disponible sur ce navigateur.'));
        setStage('error');
        return;
      }
      teardownRecognition();
      const recognition = new Ctor();
      recognition.lang = language || 'fr-FR';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognitionRef.current = recognition;

      const silenceMs = opts?.silenceMs ?? SILENCE_MS;
      const hardStopMs = opts?.hardStopMs ?? MAX_DESCRIPTION_MS;

      const armSilenceTimer = () => {
        if (silenceTimerRef.current) window.clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = window.setTimeout(() => {
          try {
            recognition.stop();
          } catch {
            /* ignore */
          }
          if (onSilence) onSilence();
        }, silenceMs);
      };

      transcriptRef.current = '';
      setTranscript('');
      setInterim('');

      recognition.onstart = () => {
        armSilenceTimer();
      };
      recognition.onresult = (ev) => {
        let finalText = '';
        let interimText = '';
        for (let i = 0; i < ev.results.length; i++) {
          const r = ev.results[i];
          if (r.isFinal) finalText += r[0].transcript;
          else interimText += r[0].transcript;
        }
        if (finalText) {
          transcriptRef.current = (transcriptRef.current + ' ' + finalText).trim();
          setTranscript(transcriptRef.current);
        }
        setInterim(interimText.trim());
        armSilenceTimer();
      };
      recognition.onerror = (ev) => {
        if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
          setAudioPermissionDenied(true);
        }
        try {
          recognition.stop();
        } catch {
          /* ignore */
        }
      };
      recognition.onend = () => {
        const final = transcriptRef.current.trim();
        if (silenceTimerRef.current) {
          window.clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        if (hardStopTimerRef.current) {
          window.clearTimeout(hardStopTimerRef.current);
          hardStopTimerRef.current = null;
        }
        onFinal(final);
      };

      hardStopTimerRef.current = window.setTimeout(() => {
        try {
          recognition.stop();
        } catch {
          /* ignore */
        }
      }, hardStopMs);

      try {
        recognition.start();
      } catch (e) {
        console.warn('[A11yVoiceGuide] recognition.start failed', e);
        setErrorMsg(t('a11y.voiceGuide.error.mic', "Impossible d'accéder au micro."));
        setStage('error');
      }
    },
    [language, t, teardownRecognition],
  );

  // ── Application des réglages ───────────────────────────────────────────

  const applySettings = useCallback((patch: Partial<A11ySettings>) => {
    const current = readA11ySettingsFromStorage();
    const next: A11ySettings = { ...current, ...patch };
    localStorage.setItem(LYRRA_A11Y_STORAGE_KEY, JSON.stringify(next));
    applyA11ySettingsToDocument(next);
    queueMicrotask(() => window.dispatchEvent(new Event(LYRRA_A11Y_UPDATED_EVENT)));
  }, []);

  const resetToDefaults = useCallback(() => {
    localStorage.setItem(LYRRA_A11Y_STORAGE_KEY, JSON.stringify(DEFAULT_A11Y_SETTINGS));
    applyA11ySettingsToDocument(DEFAULT_A11Y_SETTINGS);
    queueMicrotask(() => window.dispatchEvent(new Event(LYRRA_A11Y_UPDATED_EVENT)));
    setHasCustomSettings(false);
  }, []);

  const syncCustomSettingsFlag = useCallback(() => {
    setHasCustomSettings(hasActiveNonDefaultA11ySettings(readA11ySettingsFromStorage()));
  }, []);

  useEffect(() => {
    const onA11yUpdated = () => syncCustomSettingsFlag();
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, onA11yUpdated);
    return () => window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, onA11yUpdated);
  }, [syncCustomSettingsFlag]);

  useEffect(() => {
    if (open) syncCustomSettingsFlag();
  }, [open, syncCustomSettingsFlag]);

  // ── Flow steps ─────────────────────────────────────────────────────────

  const runListenStep = useCallback(() => {
    setStage('listening');
    setErrorMsg(null);
    startRecognition(
      async (finalText) => {
        if (!aliveRef.current) return;
        if (!finalText) {
          await playStaticPrompt('couldnt_hear');
          if (!aliveRef.current) return;
          setStage('idle');
          return;
        }
        lastUserDescriptionRef.current = finalText;
        setStage('thinking');
        try {
          const ref = await reformulateUserDescription(finalText, language, t);
          if (!aliveRef.current) return;
          setSummary(ref.summary);
          setConfirmPrompt(ref.confirmPrompt);
          setStage('confirmPlaying');
          await playDynamic(ref.confirmPrompt);
          if (!aliveRef.current) return;
          setStage('confirmListening');
          startRecognition(
            async (answer) => {
              if (!aliveRef.current) return;
              const verdict = classifyYesNo(answer);
              if (verdict === 'yes') {
                setStage('applying');
                try {
                  const userDesc = lastUserDescriptionRef.current;
                  const wantsReset = isA11yResetIntent(userDesc);
                  const rec = wantsReset
                    ? {
                        settings: DEFAULT_A11Y_SETTINGS,
                        explanation: t(
                          'a11y.voiceGuide.resetExplanation',
                          "Tous les réglages d'accessibilité ont été remis par défaut.",
                        ),
                        resetToDefaults: true,
                      }
                    : await recommendA11ySettings(userDesc, language, t);
                  if (!aliveRef.current) return;
                  if (rec.resetToDefaults || wantsReset) {
                    resetToDefaults();
                  } else {
                    applySettings(rec.settings);
                    syncCustomSettingsFlag();
                  }
                  setExplanation(rec.explanation);
                  setAppliedKeys(rec.resetToDefaults || wantsReset ? [] : Object.keys(rec.settings));
                  setStage('done');
                  await playStaticPrompt(rec.resetToDefaults || wantsReset ? 'reset_applied' : 'applied');
                  if (rec.explanation) await playDynamic(rec.explanation);
                } catch (e) {
                  console.warn('[A11yVoiceGuide] recommend failed', e);
                  setErrorMsg(
                    e instanceof Error && e.message
                      ? e.message
                      : t('a11y.voiceGuide.error.ai', 'Analyse IA indisponible.'),
                  );
                  setStage('error');
                  await playStaticPrompt('error');
                }
              } else if (verdict === 'no') {
                await playStaticPrompt('cancelled');
                setStage('idle');
              } else {
                await playStaticPrompt('not_understood');
                if (!aliveRef.current) return;
                setStage('confirmListening');
                runConfirmRetry();
              }
            },
            undefined,
            { silenceMs: 3000, hardStopMs: 15_000 },
          );
        } catch (e) {
          console.warn('[A11yVoiceGuide] reformulate failed', e);
          setErrorMsg(
            e instanceof Error && e.message
              ? e.message
              : t('a11y.voiceGuide.error.ai', 'Analyse IA indisponible.'),
          );
          setStage('error');
          await playStaticPrompt('error');
        }
      },
      undefined,
      { silenceMs: SILENCE_MS, hardStopMs: MAX_DESCRIPTION_MS },
    );
  }, [applySettings, language, playDynamic, playStaticPrompt, resetToDefaults, startRecognition, syncCustomSettingsFlag, t]);

  /** Relance simple de l'écoute de confirmation après "not_understood". */
  const runConfirmRetry = useCallback(() => {
    startRecognition(
      async (answer) => {
        if (!aliveRef.current) return;
        const verdict = classifyYesNo(answer);
        if (verdict === 'yes') {
          // On laisse le user relancer la passe complète pour rester simple
          setStage('done');
          await playStaticPrompt('applied');
        } else if (verdict === 'no') {
          await playStaticPrompt('cancelled');
          setStage('idle');
        } else {
          setStage('idle');
        }
      },
      undefined,
      { silenceMs: 3000, hardStopMs: 10_000 },
    );
  }, [playStaticPrompt, startRecognition]);

  const startGuide = useCallback(async () => {
    if (!sttSupported) {
      setErrorMsg(t('a11y.voiceGuide.error.stt', 'Reconnaissance vocale non disponible sur ce navigateur.'));
      setStage('error');
      return;
    }
    setErrorMsg(null);
    setAudioPermissionDenied(false);
    setStage('introPlaying');
    await playStaticPrompt('welcome');
    if (!aliveRef.current) return;
    runListenStep();
  }, [playStaticPrompt, runListenStep, sttSupported, t]);

  const cancelEverything = useCallback(() => {
    teardownAudio();
    teardownRecognition();
    setStage('idle');
  }, [teardownAudio, teardownRecognition]);

  // ── Lifecycle ──────────────────────────────────────────────────────────

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      teardownAudio();
      teardownRecognition();
    };
  }, [teardownAudio, teardownRecognition]);

  useEffect(() => {
    if (!open) {
      cancelEverything();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-démarrage : dès que la modal s'ouvre, on lance le guide sans attendre
  // un second clic sur le micro. On garde un garde-fou (`autoStartedRef`) pour
  // ne pas relancer si l'utilisateur a déjà déclenché manuellement, et pour
  // gérer proprement le double-mount de React StrictMode.
  const autoStartedRef = useRef<boolean>(false);
  useEffect(() => {
    if (!open) {
      autoStartedRef.current = false;
      return;
    }
    if (autoStartedRef.current) return;
    if (stage !== 'idle') return;
    autoStartedRef.current = true;
    // Petit délai pour laisser la modal s'afficher avant la requête réseau
    const id = window.setTimeout(() => {
      if (!aliveRef.current) return;
      void startGuide();
    }, 200);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelEverything();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, cancelEverything]);

  if (!open || typeof document === 'undefined') return null;

  // ── UI helpers ─────────────────────────────────────────────────────────

  const stageLabel: Record<Stage, string> = {
    idle: t('a11y.voiceGuide.stage.idle', 'Prêt à vous écouter'),
    introPlaying: t('a11y.voiceGuide.stage.intro', 'Je vous explique…'),
    listening: t('a11y.voiceGuide.stage.listening', "Je vous écoute. Parlez librement."),
    thinking: t('a11y.voiceGuide.stage.thinking', 'Analyse de vos besoins…'),
    confirmPlaying: t('a11y.voiceGuide.stage.confirmPlaying', 'Je résume…'),
    confirmListening: t('a11y.voiceGuide.stage.confirmListening', 'Dites « oui » pour confirmer ou « non » pour recommencer.'),
    applying: t('a11y.voiceGuide.stage.applying', "Application de vos réglages…"),
    done: t('a11y.voiceGuide.stage.done', "C'est fait !"),
    error: t('a11y.voiceGuide.stage.error', 'Une erreur est survenue.'),
  };

  const isListening = stage === 'listening' || stage === 'confirmListening';
  const isBusy = stage === 'introPlaying' || stage === 'thinking' || stage === 'confirmPlaying' || stage === 'applying';

  const handleMainAction = () => {
    if (stage === 'idle' || stage === 'done' || stage === 'error') {
      void startGuide();
    } else if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
    } else {
      cancelEverything();
    }
  };

  const handleResetToNormal = () => {
    if (isBusy || isListening) return;
    void (async () => {
      cancelEverything();
      resetToDefaults();
      setSummary(null);
      setConfirmPrompt(null);
      setExplanation(
        t(
          'a11y.voiceGuide.resetExplanation',
          "Tous les réglages d'accessibilité ont été remis par défaut.",
        ),
      );
      setAppliedKeys([]);
      setErrorMsg(null);
      setStage('done');
      await playStaticPrompt('reset_applied');
      if (!aliveRef.current) return;
      await playDynamic(
        t(
          'a11y.voiceGuide.resetExplanation',
          "Tous les réglages d'accessibilité ont été remis par défaut.",
        ),
      );
    })();
  };

  const showResetButton = !isBusy && !isListening;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="a11y-voice-guide-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10052] bg-black/20 backdrop-blur-[2px]"
        onClick={() => {
          cancelEverything();
          onClose();
        }}
      />
      <motion.section
        key="a11y-voice-guide-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="a11y-voice-guide-title"
        data-a11y-voice-guide="true"
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.96 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        className={cn(
          'fixed z-[10053] flex flex-col overflow-hidden rounded-card border border-[#E5DDD2] bg-[#FDFBF7] shadow-lyrra-lg',
          'inset-x-0 bottom-0 h-[min(92dvh,100%)] w-full rounded-t-card pb-[env(safe-area-inset-bottom)]',
          'md:inset-0 md:m-auto md:h-[min(92vh,40rem)] md:w-[min(96vw,32rem)] md:rounded-card md:pb-0',
        )}
      >
        <header
          data-a11y-voice-guide-header="true"
          className="relative shrink-0 border-b border-[#E5DDD2] bg-[#FDFBF7] px-4 py-4 sm:px-6 sm:py-5"
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#B38E5D] via-[#B38E5D]/50 to-transparent"
            aria-hidden
          />
          <div className="relative flex items-start justify-between gap-3 sm:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[color:color-mix(in_srgb,#B38E5D_16%,white)] shadow-sm ring-1 ring-[#B38E5D]/35 sm:h-12 sm:w-12">
              <Sparkles className="h-5 w-5 text-[#001B3D] sm:h-6 sm:w-6" />
            </span>
            <div className="min-w-0">
              <h2 id="a11y-voice-guide-title" className="text-lg font-semibold leading-tight tracking-tight text-[#001B3D] sm:text-xl">
                {t('a11y.voiceGuide.title', 'Assistant vocal accessibilité')}
              </h2>
              <p className="mt-1 text-xs text-[#5C5C5C] sm:text-sm">
                {t(
                  'a11y.voiceGuide.subtitle',
                  "Décrivez vos besoins ou demandez à remettre l'affichage normal.",
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              cancelEverything();
              onClose();
            }}
            aria-label={t('btn.close', 'Fermer')}
            className="shrink-0 rounded-xl p-2 text-[#001B3D] transition hover:bg-[#F0EBE3]"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto bg-[#FDFBF7] px-4 py-6 text-center dark:bg-[hsl(217,33%,12%)] sm:gap-5 sm:px-6 sm:py-7">
          <button
            type="button"
            onClick={handleMainAction}
            aria-label={
              stage === 'idle'
                ? t('a11y.voiceGuide.cta.start', "Démarrer l'assistant vocal")
                : isListening
                  ? t('a11y.voiceGuide.cta.stop', "Arrêter d'écouter")
                  : t('btn.cancel', 'Annuler')
            }
            className={cn(
              'relative flex h-28 w-28 items-center justify-center rounded-full transition-transform sm:h-32 sm:w-32',
              'focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-[#B38E5D]/40',
              isListening
                ? 'bg-rose-500 text-white shadow-[0_18px_60px_rgba(244,63,94,0.45)]'
                : stage === 'done'
                  ? 'bg-[#B38E5D] text-white shadow-[0_18px_60px_rgba(179,142,93,0.35)]'
                  : stage === 'error'
                    ? 'bg-[#D9534F] text-white shadow-[0_18px_60px_rgba(217,83,79,0.35)]'
                    : 'bg-[#B38E5D] text-white shadow-[0_18px_60px_rgba(179,142,93,0.35)] hover:scale-[1.02]',
            )}
          >
            {isListening && (
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-rose-500/40 animate-ping"
              />
            )}
            {stage === 'done' ? (
              <CheckCircle2 className="relative h-14 w-14" />
            ) : stage === 'error' ? (
              <AlertTriangle className="relative h-14 w-14" />
            ) : isBusy ? (
              <Volume2 className="relative h-14 w-14 animate-pulse" />
            ) : isListening ? (
              <Mic className="relative h-14 w-14" />
            ) : (
              <Mic className="relative h-14 w-14" />
            )}
          </button>

          {showResetButton && (
            <button
              type="button"
              onClick={handleResetToNormal}
              className={cn(
                'flex w-full max-w-md items-center justify-center gap-3 rounded-2xl px-5 py-4 text-sm font-bold uppercase tracking-wider transition-colors',
                'focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-[#B38E5D]/40',
                hasCustomSettings
                  ? 'bg-[#B38E5D] text-white shadow-[0_12px_30px_rgba(179,142,93,0.28)] hover:brightness-110'
                  : 'border-2 border-[#E5DDD2] bg-white text-[#001B3D] hover:bg-[#FDFBF7] dark:border-[#002952] dark:bg-[hsl(217,33%,17%)] dark:text-[#EDE6DC]',
              )}
              aria-label={t(
                'a11y.voiceGuide.resetButton',
                "Remettre tous les réglages d'accessibilité par défaut",
              )}
            >
              <RotateCcw className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('a11y.voiceGuide.resetButton', 'Remettre en normal')}
            </button>
          )}

          <div className="space-y-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C]/80 dark:text-[#E8D5BC]/70">
              {stage === 'idle' || stage === 'done' || stage === 'error'
                ? t('a11y.voiceGuide.status', 'Statut')
                : t('a11y.voiceGuide.statusActive', 'En cours')}
            </p>
            <p className="text-base font-semibold text-[#001B3D] dark:text-[#EDE6DC]">
              {stageLabel[stage]}
            </p>
          </div>

          {/* Live transcript */}
          {(transcript || interim) && (stage === 'listening' || stage === 'thinking') && (
            <div className="w-full max-w-md rounded-2xl border border-[#E5DDD2] bg-[#FDFBF7] px-4 py-3 text-left text-sm text-[#001B3D] dark:border-[#002952] dark:bg-[hsl(217,33%,17%)]/60 dark:text-[#EDE6DC]">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#5C5C5C]/80 dark:text-[#E8D5BC]/70">
                {t('a11y.voiceGuide.youSaid', 'Vous avez dit')}
              </p>
              <p className="mt-1 leading-snug">
                {transcript}
                {interim && (
                  <span className="text-[#5C5C5C]/60 dark:text-[#E8D5BC]/50"> {interim}</span>
                )}
              </p>
            </div>
          )}

          {/* Summary */}
          {summary && (stage === 'confirmPlaying' || stage === 'confirmListening') && (
            <div className="w-full max-w-md rounded-2xl border border-[#B38E5D]/25 bg-[color:color-mix(in_srgb,#B38E5D_6%,white)] px-4 py-3 text-left text-sm text-[#001B3D] dark:bg-[hsl(217,33%,17%)]/70 dark:text-[#EDE6DC]">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#B38E5D]">
                {t('a11y.voiceGuide.reformulation', "Voici ce que j'ai compris")}
              </p>
              <p className="mt-1 leading-snug">{summary}</p>
              {confirmPrompt && (
                <p className="mt-2 text-[12px] italic text-[#5C5C5C]/80 dark:text-[#E8D5BC]/70">
                  {confirmPrompt}
                </p>
              )}
            </div>
          )}

          {/* Done state */}
          {stage === 'done' && (
            <div className="w-full max-w-md rounded-card border border-[#E5DDD2] bg-[#EDE6DC] px-4 py-3 text-left text-sm text-[#001B3D] dark:border-[#002952] dark:bg-[color:color-mix(in_srgb,#B38E5D_18%,hsl(217,33%,17%))] dark:text-[#EDE6DC]">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#B38E5D] dark:text-[#E8D5BC]">
                {t('a11y.voiceGuide.applied', 'Réglages appliqués')}
              </p>
              {explanation && <p className="mt-1 leading-snug">{explanation}</p>}
              {appliedKeys.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {appliedKeys.map((k) => (
                    <li
                      key={k}
                      className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-semibold text-[#001B3D] dark:bg-[hsl(217,33%,20%)] dark:text-[#EDE6DC]"
                    >
                      {k}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Error state */}
          {(stage === 'error' || errorMsg || audioPermissionDenied) && (
            <div className="w-full max-w-md rounded-card border border-[#E5DDD2] bg-[#FDF2DE] px-4 py-3 text-left text-sm text-[#7A5A1F] dark:border-[#002952] dark:bg-[color:color-mix(in_srgb,#D9534F_12%,hsl(217,33%,17%))] dark:text-[#EDE6DC]">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em]">
                {t('a11y.voiceGuide.problem', 'Problème')}
              </p>
              <p className="mt-1 leading-snug">
                {audioPermissionDenied
                  ? t(
                      'a11y.voiceGuide.error.micDenied',
                      "L'accès au microphone a été refusé. Autorisez-le dans les paramètres du navigateur.",
                    )
                  : errorMsg ||
                    t('a11y.voiceGuide.error.generic', "Quelque chose s'est mal passé. Réessayez ou ajustez les réglages manuellement.")}
              </p>
            </div>
          )}
        </div>

        <footer
          data-a11y-voice-guide-footer="true"
          className="flex shrink-0 items-center justify-between gap-3 border-t border-[#F0EBE3] bg-[#FDFBF7] px-6 py-4 dark:border-[#002952] dark:bg-[hsl(217,33%,15%)]"
        >
          <button
            type="button"
            onClick={() => {
              cancelEverything();
              onClose();
            }}
            className="rounded-btn border border-[#E5DDD2] bg-white px-4 py-2 text-[12px] font-semibold text-[#001B3D] transition hover:bg-[#F0EBE3] dark:border-[#002952] dark:bg-[hsl(217,33%,17%)] dark:text-[#EDE6DC] dark:hover:bg-[hsl(217,33%,22%)]"
          >
            {t('btn.close', 'Fermer')}
          </button>

          <div className="flex items-center gap-2 text-[11px] text-[#5C5C5C]/80 dark:text-[#E8D5BC]/70">
            {sttSupported ? (
              <Mic className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <MicOff className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {sttSupported
              ? t('a11y.voiceGuide.micReady', 'Micro prêt')
              : t('a11y.voiceGuide.micUnsupported', 'Reconnaissance vocale non supportée')}
          </div>
        </footer>
      </motion.section>
    </AnimatePresence>,
    document.body,
  );
};

export default A11yVoiceGuide;
